import type { RuteroLuggageSelection } from '@bandido/shared/types';
import type { TFunction } from 'i18next';

export interface TransferLuggageDraft {
  mode: 'later' | 'none' | 'specified';
  checked: string;
  carryOn: string;
}

export const emptyTransferLuggage = (): TransferLuggageDraft => ({
  mode: 'later',
  checked: '',
  carryOn: '',
});

export function luggageQuantity(value: string): number | null {
  if (!/^\d+$/.test(value)) return null;
  const quantity = Number(value);
  return Number.isSafeInteger(quantity) ? quantity : null;
}

/** undefined = unknown; null = incomplete/invalid; zero must be explicit. */
export function transferLuggageSelection(draft: TransferLuggageDraft): RuteroLuggageSelection | undefined | null {
  if (draft.mode === 'later') return undefined;
  if (draft.mode === 'none') return { checked: 0, carryOn: 0 };
  const checked = luggageQuantity(draft.checked);
  const carryOn = luggageQuantity(draft.carryOn);
  return checked === null || carryOn === null ? null : { checked, carryOn };
}

export function transferLuggageFromParams(params: Record<string, string>): TransferLuggageDraft {
  if (params.equipaje === 'none') return { mode: 'none', checked: '0', carryOn: '0' };
  if (params.equipaje === 'specified' || params.valijas !== undefined || params.mano !== undefined) {
    return { mode: 'specified', checked: params.valijas ?? '', carryOn: params.mano ?? '' };
  }
  // Unknown modes must not silently turn an invalid shared link into another search.
  if (params.equipaje && params.equipaje !== 'later') {
    return { mode: 'specified', checked: '', carryOn: '' };
  }
  return emptyTransferLuggage();
}

export function transferLuggageToParams(draft: TransferLuggageDraft): Record<string, string | undefined> {
  return {
    equipaje: draft.mode === 'later' ? undefined : draft.mode,
    valijas: draft.mode === 'specified' ? draft.checked : undefined,
    mano: draft.mode === 'specified' ? draft.carryOn : undefined,
  };
}

export function transferLuggageSummary(luggage: RuteroLuggageSelection | undefined | null, t: TFunction): string {
  if (!luggage) return '';
  return t(
    luggage.checked === 0 && luggage.carryOn === 0
      ? 'travelServices.transfers.luggage.none'
      : 'travelServices.transfers.luggage.summary',
    { ...luggage },
  );
}
